// Cities master client.
//
// Replaces the hardcoded lists that used to live in
//   src/assets/json/cities.json
//   src/assets/json/doctor/doctorarea.json
//   indexbanner.vue defaultcitySuggestion
//
// Everything here is plain data + pure helpers so it can be reused by the
// search box, the doctor forms, the patient profile and the admin screen.
import axios from "axios";

// Sentinel value for the "Other / Enter manually" dropdown entry. Kept out of
// the numeric city_id space so it can never collide with a real city.
export const OTHER_CITY_VALUE = "__other__";
export const OTHER_CITY_LABEL = "Other / Enter manually";

export const CITY_NAME_MAX_LENGTH = 100;

// Pseudo-entry for the home/search city box meaning "do not filter by city".
export const ALL_CITIES_LABEL = "All Cities";
export const ALL_CITIES_OPTION = { city_id: 0, name: ALL_CITIES_LABEL };

/** True when the value is the "All Cities" sentinel rather than a real city. */
export function isAllCities(name) {
  return normalizeCityName(name) === normalizeCityName(ALL_CITIES_LABEL);
}

/**
 * Fold a city name to its duplicate-detection form.
 * Mirrors the backend's normalize() so "Mangalore", " mangalore " and
 * "MANGALORE" all compare equal on both sides.
 */
export function normalizeCityName(name) {
  if (name === null || name === undefined) return "";
  return String(name).trim().replace(/\s+/g, " ").toLowerCase();
}

/** Trim and collapse inner whitespace, preserving the user's capitalisation. */
export function cleanCityName(name) {
  if (name === null || name === undefined) return "";
  return String(name).trim().replace(/\s+/g, " ");
}

/**
 * Validate a manually entered city name.
 * Returns an error message, or "" when the value is acceptable.
 */
export function validateManualCityName(name) {
  const cleaned = cleanCityName(name);
  if (!cleaned) return "Please enter a city name.";
  if (cleaned.length > CITY_NAME_MAX_LENGTH) {
    return `City name must not exceed ${CITY_NAME_MAX_LENGTH} characters.`;
  }
  if (!/^[\p{L}\p{M}][\p{L}\p{M}\s.'-]*$/u.test(cleaned)) {
    return "City name may only contain letters, spaces, apostrophes, hyphens and periods.";
  }
  return "";
}

/** Find a loaded city whose name matches `name`, ignoring case and whitespace. */
export function findCityByName(cities, name) {
  const target = normalizeCityName(name);
  if (!target) return null;
  return (cities || []).find(city => normalizeCityName(city.name) === target) || null;
}

/** GET /cities - active cities only, ordered by name. */
export async function fetchCities() {
  const response = await axios.get("/cities");
  return response.data || [];
}

// The last city the user picked or was located in, restored on the next visit.
const LAST_CITY_KEY = "yd_last_city";

/**
 * The remembered { city_id, name, place_id, locality }, or null. Never throws.
 * `name` is the city; `locality` is set when a locality was picked. city_id is
 * missing for a gazetteer city we do not serve yet.
 */
export function readLastCity() {
  try {
    const city = JSON.parse(localStorage.getItem(LAST_CITY_KEY));
    return city && city.name && (city.city_id || city.place_id) ? city : null;
  } catch (error) {
    return null;
  }
}

/** Remember a city, or forget it when given null / "All Cities". Never throws. */
export function saveLastCity(city) {
  try {
    if (city && city.name && (city.city_id || city.place_id)) {
      const { city_id, name, place_id, locality } = city;
      localStorage.setItem(LAST_CITY_KEY, JSON.stringify({ city_id, name, place_id, locality }));
    } else {
      localStorage.removeItem(LAST_CITY_KEY);
    }
  } catch (error) {
    // Storage blocked (private mode etc.) - remembering the city is optional.
  }
}

/**
 * Ask the browser for the user's position.
 * Resolves to { latitude, longitude }. Rejects with an Error whose `reason` is
 * one of "unsupported", "denied", "unavailable" or "timeout", so callers can
 * show a translated message. Only works on HTTPS or localhost.
 */
export function getCurrentPosition() {
  const fail = reason => Object.assign(new Error(`Geolocation ${reason}`), { reason });
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(fail("unsupported"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ latitude: coords.latitude, longitude: coords.longitude }),
      error => {
        const reasons = { 1: "denied", 2: "unavailable", 3: "timeout" };
        reject(fail(reasons[error.code] || "unavailable"));
      },
      // City-level accuracy is enough; a cached fix up to 10 minutes old is fine.
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
    );
  });
}

/**
 * GET /cities/nearest - the active city closest to a point.
 * Coordinates are rounded to ~1 km before leaving the browser: that is plenty
 * to pick a city and keeps precise locations out of request logs.
 * Resolves to null when no city we serve is nearby.
 */
export async function fetchNearestCity({ latitude, longitude }) {
  const round = value => Math.round(value * 100) / 100;
  try {
    const response = await axios.get("/cities/nearest", {
      params: { latitude: round(latitude), longitude: round(longitude) }
    });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) return null;
    throw error;
  }
}

// --- places gazetteer: every city and locality (doc.api docs/location-search.md)

/** Typed letters before the location box starts asking the API. */
export const PLACE_QUERY_MIN_LENGTH = 2;

/**
 * GET /places/suggest - cities (2 letters) and localities (3+) matching what
 * was typed; an exact city name returns the city followed by its localities.
 * Each item: { place_id, kind: "city"|"locality", name, label, city_name,
 * city_id (null when we do not serve it), state, district, pincode, ... }.
 * Pass an AbortController signal to cancel a superseded request.
 */
export async function fetchPlaceSuggestions(query, { signal, limit = 10 } = {}) {
  const response = await axios.get("/places/suggest", { params: { q: query, limit }, signal });
  return response.data || [];
}

/**
 * GET /places/nearest - the locality (else city) at a point, for "Use my
 * location". Coordinates are rounded to ~100 m: enough to pick a locality.
 * Resolves to null when nothing is within 50 km (e.g. outside India).
 */
export async function fetchNearestPlace({ latitude, longitude }) {
  const round = value => Math.round(value * 1000) / 1000;
  try {
    const response = await axios.get("/places/nearest", {
      params: { latitude: round(latitude), longitude: round(longitude) }
    });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) return null;
    throw error;
  }
}

/** What the location box should remember for a picked place (see saveLastCity). */
export function placeToLastCity(place) {
  return {
    city_id: place.city_id || null,
    name: place.city_name || place.name,
    place_id: place.place_id,
    locality: place.kind === "locality" ? place.name : null
  };
}

/**
 * POST /cities - resolve a manually entered city.
 * The backend returns the existing record when one matches, so this never
 * creates a duplicate of an existing city.
 */
export async function resolveCity({ name, state = null, country = "India" }) {
  const response = await axios.post("/cities", {
    name: cleanCityName(name),
    state,
    country
  });
  return response.data;
}
